<template>
  <div>
    <Collapse :label-open="$t('Toon filters')" :label-closed="$t('Verberg filters')" class="hide-desktop" v-if="Object.keys(filters).length">
      <slot name="before-filters" />
      <FormGroup>
        <Input v-for="(filterData, filter) in filters" :key="filter" v-model="filterData.filter" :placeholder="filterData.filterPlaceholder" :label="filterData.label" />
      </FormGroup>
    </Collapse>
    <div class="hide-mobile">
      <slot name="before-filters" />
    </div>
    <FormGroup v-if="bulk && Array.isArray(bulk)">
      <Grid style="align-items: center;" :columns="{ default: 'repeat(3, max-content)' }">
        {{ $t('Met geselecteerde') }}: <Select :options="bulk" style="margin: 0;" simple v-model="bulkMethod" required></Select><Button fill success @click="handleBulk" :disabled="!bulkMethod || selectedRecords.length === 0">{{ bulkLabel }}</Button>
      </Grid>
    </FormGroup>
    <div class="table-responsive">
      <table :class="{ simple: simple }">
        <thead>
        <tr>
          <th v-if="bulk"><Checkbox @input="triggerBulk" v-model="bulkChecked" /></th>
          <th v-for="(headerData, header) in headers" :key="header" :style="{ width: headerData.width }" @click="handleSort(header)" :class="{ sorted: sortBy === header }">
            <div class="d-flex align-items-center justify-content-between">
              <Input @click.stop v-model="headerData.filter" :placeholder="headerData.filterPlaceholder" :label="headerData.label" v-if="'filter' in headerData" />
              <span v-else>{{ headerData.label }}</span>
              <Icon style="margin-left: 0.5rem;" v-if="headerData.sortable" :name="header === sortBy && sortMethod === 'asc' ? 'sort-up' : header === sortBy ? 'sort-down' : 'sort'" />
            </div>
          </th>
        </tr>
        </thead>
        <tfoot v-if="!simple && !noFooter">
        <tr>
          <th v-if="bulk"><Checkbox @input="triggerBulk" v-model="bulkChecked" /></th>
          <th v-for="(headerData, header) in headers" :key="header" :style="{ width: headerData.width }" @click="handleSort(header)" :class="{ sorted: sortBy === header }">
            <div class="d-flex align-items-center justify-content-between">
              <Input v-model="headerData.filter" :placeholder="headerData.filterPlaceholder" :label="headerData.label" v-if="'filter' in headerData" />
              <span v-else>{{ headerData.label }}</span>
              <Icon style="margin-left: 0.5rem;" v-if="headerData.sortable" :name="header === sortBy && sortMethod === 'asc' ? 'sort-up' : header === sortBy ? 'sort-down' : 'sort'" />
            </div>
          </th>
        </tr>
        </tfoot>
        <tbody>
        <tr v-for="(record, index) in paginatedRecords" :key="index" :class="{ subheader: record.subheader }" :style="{ background: record.customBg }">
          <td :data-th="$t('Selecteren')" v-if="bulk"><Checkbox v-if="!record.disableCheckbox" v-model="record.selected" :value="record" /></td>
          <td v-if="record.subheader" :colspan="Object.keys(headers).length">{{ record.title }}</td>
          <td :data-th="headerData.label" v-else v-for="(headerData, header) in headers" :key="header">
            <slot :name="'data-' + header" :data="record[header]" :record="record">
              <a href="#" v-if="headerData.action" @click.prevent="headerData.action(record)" v-html="record[header]"></a>
              <span v-else v-html="record[header]"></span>
            </slot>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <Pagination v-if="!noPagination" :perPage="pagination.perPage" :total="pagination.total" v-model="page" />
  </div>
</template>

<script>
import Pagination from '../Core/Pagination'
import Input from '../Core/Input'
import Icon from '../Core/Icon'
import Checkbox from '../Core/Checkbox'
import Select from '../Core/Select'
import Button from '../Core/Button'
import Grid from '../Core/Grid'
import FormGroup from '../Core/FormGroup'
import Collapse from '../Core/Collapse'
export default {
  name: 'Table',
  components: {
    Collapse,
    FormGroup,
    Grid,
    Button,
    Select,
    Checkbox,
    Icon,
    Pagination,
    Input
  },
  props: {
    headers: Object,
    records: Array,
    perPage: {
      type: Number,
      default: 50
    },
    bulk: [Array, Boolean],
    noPagination: Boolean,
    simple: Boolean,
    noFooter: Boolean
  },
  mounted () {
    this.manipulateRecords()
  },
  data () {
    return {
      page: 1,
      sortBy: false,
      sortMethod: 'asc',
      bulkChecked: false,
      manipulatedRecords: [],
      bulkMethod: false
    }
  },
  computed: {
    hasBulk () {
      return this.$slots.bulk
    },
    bulkLabel () {
      if (Array.isArray(this.bulk)) {
        const bulk = this.bulk.find(tmp => {
          return tmp.value === this.bulkMethod
        })
        if (bulk) {
          return bulk.label
        }
      }
      return this.$t('Maak een keuze')
    },
    activeFilters () {
      const filters = {}
      for (const header in this.headers) {
        const headerData = this.headers[header]
        if (headerData.filter) {
          filters[header] = {
            value: headerData.filter
          }
          if ('filterFunction' in headerData) {
            filters[header].method = headerData.filterFunction
          }
        }
      }
      return filters
    },
    filteredRecords () {
      return this.manipulatedRecords.filter(record => {
        if (record.subheader) return true

        for (const filter in this.activeFilters) {
          const filterData = this.activeFilters[filter]
          if (typeof filterData.method === 'function') {
            if (filterData.method(record[filter], this.activeFilters[filter].value) === false) return false
            continue
          }
          const filterValue = this.activeFilters[filter].value.toString().toLowerCase().trim()
          const recordValue = record[filter].toString().toLowerCase().trim()

          if (!recordValue.includes(filterValue)) return false
        }
        return true
      }).sort((a, b) => {
        if (!this.sortBy) return 0

        if (typeof this.headers[this.sortBy].sortFunction === 'function') {
          return this.headers[this.sortBy].sortFunction(a[this.sortBy], b[this.sortBy], this.sortMethod)
        }

        const compareValueA = a[this.sortBy]
        const compareValueB = b[this.sortBy]

        if (typeof compareValueA !== 'string' || typeof compareValueB !== 'string') {
          return -1
        }

        if (this.sortMethod === 'asc') {
          return compareValueA.localeCompare(compareValueB)
        } else {
          return compareValueB.localeCompare(compareValueA)
        }
      })
    },
    recordsOnly () {
      return this.filteredRecords.filter(record => {
        return !record.subheader
      })
    },
    paginatedRecords () {
      if (this.noPagination) {
        return this.filteredRecords
      }
      let counter = -1
      const start = (this.page - 1) * this.pagination.perPage - 1
      const max = start + this.pagination.perPage

      return this.filteredRecords.filter(record => {
        if (record.subheader) return true
        counter++
        if (counter <= start) return false
        if (counter >= max) return false
        return true
      })
    },
    pagination () {
      return {
        perPage: this.perPage,
        total: this.recordsOnly.length
      }
    },
    selectedRecords () {
      const records = []
      for (const record of this.manipulatedRecords) {
        if (record.selected) records.push(record)
      }
      return records
    },
    filters () {
      const filters = {}
      for (const header in this.headers) {
        const headerData = this.headers[header]
        if ('filter' in headerData) {
          filters[header] = headerData
        }
      }
      return filters
    }
  },
  methods: {
    setFilter (header, value) {
      const filter = this.filters.find(filter => {
        return filter.header === header
      })
      if (filter) {
        filter.value = value
      } else {
        this.filters.push({
          header,
          value
        })
      }
    },
    handleSort (header) {
      if (this.headers[header].sortable) {
        if (this.sortBy === header) {
          if (this.sortMethod === 'asc') {
            this.sortMethod = 'desc'
          } else {
            this.sortBy = false
            this.sortMethod = 'asc'
          }
        } else {
          this.sortBy = header
          this.sortMethod = 'asc'
        }
      }
    },
    triggerBulk () {
      for (const record of this.paginatedRecords) {
        if (!record.disableCheckbox) record.selected = this.bulkChecked
      }
    },
    manipulateRecords () {
      const records = []
      for (const record of this.records) {
        records.push({
          ...record,
          selected: !!record.selected
        })
      }
      this.manipulatedRecords = records
    },
    handleBulk () {
      const bulkMethod = this.bulk.find(method => {
        return method.value === this.bulkMethod
      })
      if (bulkMethod && typeof bulkMethod.action === 'function') {
        bulkMethod.action(this.selectedRecords)
      }
    },
    getSelectedRecords () {
      return this.selectedRecords
    }
  },
  watch: {
    records () {
      this.manipulateRecords()
    },
    selectedRecords () {
      this.$emit('selected-records-updated', this.selectedRecords)
      if (this.selectedRecords.length === this.manipulatedRecords.length) {
        this.bulkChecked = true
      } else {
        this.bulkChecked = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.table-responsive {
  margin-bottom: 2rem;
  border-radius: var(--border-radius);
  display: block;
  overflow-x: auto;

  /* width */
  &::-webkit-scrollbar {
    height: 5px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}
@media (max-width: 767px) {
  .table-responsive .dropdown-options {
    position: static !important;
  }
}
@media (min-width: 768px) {
  .table-responsive {
    overflow: visible;
  }
}

th {
  font-weight: 600;
  text-align: left;
  padding: 1rem;
  background: var(--gray);

  &.sorted {
    background: var(--primary-color);
    color: var(--primary-bg);
  }

  p,
  .form-group {
    margin: 0;
  }
}
thead th {
  &:first-child {
    border-top-left-radius: var(--border-radius);
  }
  &:last-child {
    border-top-right-radius: var(--border-radius);
  }

  i {
    align-self: flex-end;
    line-height: 40px !important;
  }
}
td {
  padding: 1rem;
}
table {
  width: 100%;
}
table .subheader td {
  background: var(--gray-dark);
  font-weight: bold;
  padding: 1rem;
}
tbody tr {
  border-bottom: 1px solid var(--gray-dark);
  transition: background 300ms ease-in-out;

  &:first-child {
    border-top: 1px solid var(--gray-dark);
  }

  &:hover {
    background: var(--gray-lighter);
  }
}
.simple {
  th {
    background: none;
    font-weight: normal;
  }
  tbody tr {
    border-bottom: 0;

    &:first-child {
      border: 0;
    }
  }
  td {
    padding: 0.5rem;
  }
}
@media screen and (max-width: 768px) {
  th {
    display: none;
  }
  td {
    display: block;
    padding: 0.5rem 1rem;

    &:first-child {
      padding-top: 1rem;
    }

    &:last-child {
      padding-bottom: 1rem;
    }

    &::before {
      content: attr(data-th);
      display: block;
      font-weight: bold;
    }
  }
}
</style>
